import AppDescription from '@nxsys/components/AppDescription';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import {
  Payment,
  PaymentParams,
} from '@nxsys/store/slices/paymentSetting/types';
import { Descriptions, Tag } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PaymentForm, { PaymentFormRef, PaymentFormValues } from '../components/PaymentForm';
import { Icons } from '@nxsys/types/icons';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { paymentSettingActions } from '@nxsys/store/slices/paymentSetting/reducer/paymentSettingSlice';
import AppLoader from '@nxsys/components/AppLoader';
import { useSelector } from 'react-redux';
import { IRELAND } from '@nxsys/store/constants/keys';

type ViewPaymentProps = {
  isOpen: boolean;
  toggle: () => void;
  paymentId: string | undefined;
  refetchData: () => void;
};

const ViewPaymentModal: React.FC<ViewPaymentProps> = ({
  isOpen,
  toggle,
  paymentId,
  refetchData,
}: ViewPaymentProps) => {
  const { messages } = useTypedIntl();

  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const dispatch = useDispatch<AppDispatch>();

  const formRef = useRef<PaymentFormRef>(null);

  const [data, setData] = useState<Payment | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isEditing, setIsEditing] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getDetail = async (id: string) => {
    setIsLoadingData(true);
    const res = await dispatch(
      paymentSettingActions.getPaymentById({ id }),
    ).unwrap();
    setData(res.data);
    setIsLoadingData(false);
  };

  useEffect(() => {
    if (paymentId && isOpen) getDetail(paymentId);
  }, [isOpen, paymentId]);

  const handleSubmit = (values: PaymentFormValues) => {
    if (!data?.id) return;

    setIsSubmitting(true);
    let params: PaymentParams;
    // NX-5228: country decides the payload shape, so it is tested first — the
    // matching create path (CreatePaymentModal) already does. Checking
    // `!isBureau` first sent an umbrella-Ireland edit the UK-shaped
    // beforeTax/beforeNI/beforePension set and dropped every PRSI/USC/ASC flag.
    if (isIreland) {
      params = {
        description: values.description ?? '',
        taxCanBeDeducted: Boolean(values.taxCanBeDeducted),
        employeePRSICanBeDeducted: Boolean(values.employeePRSICanBeDeducted),
        employerPRSICanBeDeducted: Boolean(values.employerPRSICanBeDeducted),
        uscCanBeDeducted: Boolean(values.uscCanBeDeducted),
        ascCanBeDeducted: Boolean(values.ascCanBeDeducted),
        pensionSchemaDeductionCanBeDeducted: Boolean(values.pensionSchemaDeductionCanBeDeducted),
        includeInCisCalculation: Boolean(values.includeInCisCalculation),
      };
    } else if (!isBureau) {
      params = {
        description: values.description ?? '',
        includeInCisCalculation: Boolean(values.includeInCisCalculation),
        beforeTax: Boolean(values.beforeTax),
        beforeNI: Boolean(values.beforeNI),
        beforePension: Boolean(values.beforePension),
      };
    } else {
      params = {
        description: values.description ?? '',
        beforeTax: Boolean(values.beforeTax),
        beforeNI: Boolean(values.beforeNI),
        beforePension: Boolean(values.beforePension),
      };
    }
    dispatch(
      paymentSettingActions.updatePaymentById({
        id: data.id,
        payload: params,
      }),
    )
      .then((res) => {
        refetchData();
        setData((res.payload as any)?.data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const descriptionItems = [
    {
      label: messages['paymentSetting.payment.paymentCode'],
      value: data?.paymentCode ?? '',
    },
    {
      label: messages['paymentSetting.payment.description'],
      value: data?.description ?? '',
    },
    ...(!isIreland
      ? [
          ...(!isBureau
            ? [
                {
                  label: messages['paymentSetting.payment.beforeCisTax'],
                  value: (
                    <Tag>
                      {data?.includeInCisCalculation
                        ? messages['common.yes']
                        : messages['common.no']}
                    </Tag>
                  ),
                },
              ]
            : []),
          {
            label: messages['paymentSetting.payment.beforeTax'],
            value: (
              <Tag>
                {data?.beforeTax
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeNI'],
            value: (
              <Tag>
                {data?.beforeNI
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforePension'],
            value: (
              <Tag>
                {data?.beforePension
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
        ]
      : [
          {
            label: messages['paymentSetting.payment.beforeTax'],
            value: (
              <Tag>
                {data?.taxCanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeEmployeePRSI'],
            value: (
              <Tag>
                {data?.employeePRSICanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeEmployerPRSI'],
            value: (
              <Tag>
                {data?.employerPRSICanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeUSC'],
            value: (
              <Tag>
                {data?.uscCanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeASC'],
            value: (
              <Tag>
                {data?.ascCanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label:
              messages['paymentSetting.payment.beforePensionSchemeDeductions'],
            value: (
              <Tag>
                {data?.pensionSchemaDeductionCanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeRCT'],
            value: (
              <Tag>
                {data?.includeInCisCalculation
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
        ]),
  ];

  return (
    <AppModalContainer
      title={messages['paymentSetting.payment.paymentDetail']}
      isOpen={isOpen}
      toggle={toggle}
      actions={
        isEditing
          ? [
              {
                name: messages['common.cancel'],
                variant: 'outlined',
                onClick: () => setIsEditing(false),
              },
              {
                name: messages['common.save'],
                buttonType: 'primary',
                onClick: () => formRef.current?.submit(),
                loading: isSubmitting,
                disabled: isSubmitting,
              },
            ]
          : [
              {
                name: messages['common.edit'],
                buttonType: 'primary',
                leftIcon: (
                  <Icons.IconEdit color={defaultTheme.theme.palette.white} />
                ),
                onClick: () => setIsEditing(true),
              },
            ]
      }
    >
      {(isLoadingData || isSubmitting) && <AppLoader />}
      {data && (
        <>
          {!isEditing ? (
            <AppDescription
              column={1}
              labelStyle={{ width: 216 }}
              bordered={false}
              colon={false}
              size="middle"
            >
              {descriptionItems?.map((item, _index) => (
                <Descriptions.Item key={_index} label={item.label} span={1}>
                  {item.value ?? '-'}
                </Descriptions.Item>
              ))}
            </AppDescription>
          ) : (
            <PaymentForm
              formRef={formRef}
              onSubmit={handleSubmit}
              defaultValues={{
                paymentCode: data?.paymentCode,
                description: data?.description,
                ...(!isBureau || isIreland
                  ? {
                      includeInCisCalculation: data?.includeInCisCalculation,
                    }
                  : {}),
                ...(!isIreland
                  ? {
                      beforeTax: data?.beforeTax,
                      beforeNI: data?.beforeNI,
                      beforePension: data?.beforePension,
                    }
                  : {
                      taxCanBeDeducted: data?.taxCanBeDeducted,
                      employeePRSICanBeDeducted:
                        data?.employeePRSICanBeDeducted,
                      employerPRSICanBeDeducted:
                        data?.employerPRSICanBeDeducted,
                      uscCanBeDeducted: data?.uscCanBeDeducted,
                      ascCanBeDeducted: data?.ascCanBeDeducted,
                      pensionSchemaDeductionCanBeDeducted:
                        data?.pensionSchemaDeductionCanBeDeducted,
                    }),
              }}
            />
          )}
        </>
      )}
    </AppModalContainer>
  );
};

export default ViewPaymentModal;
